import { useState, useEffect } from 'react';
import { organizationsAPI } from '../lib/api';
import { toast } from 'react-hot-toast';
import {
    Building2,
    Search,
    MapPin,
    Mail,
    Phone,
    Edit2,
    Trash2,
    X,
    Loader2
} from 'lucide-react';

interface Organization {
    _id: string;
    name: string;
    address: {
        fullAddress: string;
        city: string;
        state: string;
        pincode: string;
        location?: {
            type: string;
            coordinates: number[];
        };
    };
    contactEmail: string;
    contactPhone: string;
    status: string;
    createdAt: string;
}

export default function Organizations() {
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        contactEmail: '',
        contactPhone: '',
        address: {
            fullAddress: '',
            city: '',
            state: '',
            pincode: '',
            contactName: '',
            contactPhone: '',
            type: 'work',
            location: { type: 'Point', coordinates: [0, 0] }
        }
    });

    useEffect(() => {
        fetchOrganizations();
    }, []);

    const fetchOrganizations = async () => {
        try {
            setLoading(true);
            const response = await organizationsAPI.getAll();
            setOrganizations(response.data);
        } catch (error: any) {
            toast.error('Failed to fetch organizations');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const submissionData = {
                ...formData,
                address: {
                    ...formData.address,
                    contactName: formData.address.contactName || formData.name,
                    contactPhone: formData.address.contactPhone || formData.contactPhone,
                }
            };

            if (editingOrg) {
                await organizationsAPI.update(editingOrg._id, submissionData);
                toast.success('Organization updated successfully');
            }
            setIsModalOpen(false);
            setEditingOrg(null);
            resetForm();
            fetchOrganizations();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            contactEmail: '',
            contactPhone: '',
            address: {
                fullAddress: '',
                city: '',
                state: '',
                pincode: '',
                contactName: '',
                contactPhone: '',
                type: 'work',
                location: { type: 'Point', coordinates: [0, 0] }
            }
        });
    };

    const handleEdit = (org: Organization) => {
        setEditingOrg(org);
        setFormData({
            name: org.name,
            contactEmail: org.contactEmail,
            contactPhone: org.contactPhone,
            address: {
                fullAddress: org.address?.fullAddress || '',
                city: org.address?.city || '',
                state: org.address?.state || '',
                pincode: org.address?.pincode || '',
                contactName: org.name,
                contactPhone: org.contactPhone,
                type: 'work',
                location: org.address?.location || { type: 'Point', coordinates: [0, 0] }
            }
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this organization?')) {
            try {
                await organizationsAPI.delete(id);
                toast.success('Organization deleted successfully');
                fetchOrganizations();
            } catch (error: any) {
                toast.error('Failed to delete organization');
            }
        }
    };

    const filteredOrgs = organizations.filter(org =>
        org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.contactEmail.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Organizations</h1>
                    <p className="text-gray-500">Manage vendors and partners on the platform</p>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search organizations..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Loader2 className="animate-spin text-green-600 mb-4" size={40} />
                        <p className="text-gray-500">Loading organizations...</p>
                    </div>
                ) : filteredOrgs.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                        {filteredOrgs.map((org) => (
                            <div key={org._id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow relative group">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="bg-green-100 p-3 rounded-lg text-green-600">
                                        <Building2 size={24} />
                                    </div>
                                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => handleEdit(org)}
                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                        >
                                            <Edit2 size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(org._id)}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>

                                <h3 className="text-lg font-bold text-gray-900 mb-1">{org.name}</h3>
                                <div className="space-y-2 text-sm text-gray-600">
                                    <div className="flex items-center gap-2">
                                        <Mail size={16} className="text-gray-400" />
                                        {org.contactEmail}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Phone size={16} className="text-gray-400" />
                                        {org.contactPhone}
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <MapPin size={16} className="text-gray-400 mt-1 flex-shrink-0" />
                                        <span>
                                            {org.address?.fullAddress}, {org.address?.city}, {org.address?.state} - {org.address?.pincode}
                                        </span>
                                    </div>
                                </div>

                                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${org.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                            }`}>
                                            {org.status.toUpperCase()}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => {
                                            localStorage.setItem('selectedOrg', JSON.stringify(org));
                                            window.location.href = '/stores';
                                        }}
                                        className="text-xs font-bold text-green-600 hover:text-green-700 flex items-center gap-1"
                                    >
                                        Manage Stores
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <Building2 className="mx-auto text-gray-300 mb-4" size={64} />
                        <h3 className="text-lg font-medium text-gray-900">No organizations found</h3>
                        <p className="text-gray-500">Try adjusting your search.</p>
                    </div>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="modal-overlay backdrop-blur-sm">
                    <div className="modal-content max-w-2xl">
                        <div className="flex items-center justify-between p-6 border-b border-gray-100">
                            <h2 className="text-xl font-bold text-gray-900">
                                {editingOrg ? 'Edit Organization' : 'Add New Organization'}
                            </h2>
                            <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="col-span-2">
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Organization Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                        placeholder="e.g. Freshio Global"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Contact Email</label>
                                    <input
                                        type="email"
                                        required
                                        value={formData.contactEmail}
                                        onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                        placeholder="contact@org.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Contact Phone</label>
                                    <input
                                        type="tel"
                                        required
                                        value={formData.contactPhone}
                                        onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                        placeholder="+91 98765 43210"
                                    />
                                </div>

                                <div className="col-span-2 space-y-4">
                                    <h3 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2">Address Details</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="col-span-2">
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Full Address</label>
                                            <textarea
                                                required
                                                value={formData.address.fullAddress}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    address: { ...formData.address, fullAddress: e.target.value }
                                                })}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none h-20 resize-none transition-all"
                                                placeholder="Street, Area, Landmark"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">City</label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.address.city}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    address: { ...formData.address, city: e.target.value }
                                                })}
                                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                                placeholder="City"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">State</label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.address.state}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    address: { ...formData.address, state: e.target.value }
                                                })}
                                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                                placeholder="State"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Pincode</label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.address.pincode}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    address: { ...formData.address, pincode: e.target.value }
                                                })}
                                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                                placeholder="6-digit Pincode"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                        <div className="p-6 border-t border-gray-100 bg-gray-50 flex gap-4">
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="flex-1 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="flex-1 py-3 bg-[#10b981] text-white rounded-xl font-bold hover:bg-[#059669] transition-all shadow-lg shadow-[#10b981]/20"
                            >
                                {editingOrg ? 'Update Organization' : 'Create Organization'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
